import React, { useState, useEffect } from 'react'
import { Search } from 'lucide-react'
import { performHolisticSEOAudit } from './utils/seoAudit'
import { isValidUrl, normalizeUrl } from './utils/urlValidation'
import SEOAudit from './components/SEOAudit'
import ProgressBar from './components/ProgressBar'
import Footer from './components/Footer'
import Header from './components/Header'
import { PageAuditResult } from './types'
import FullPageLoader from './components/FullPageLoader'

const App: React.FC = () => {
  const [formData, setFormData] = useState({
    url: '',
    name: '',
    email: '',
    phone: ''
  })
  const [step, setStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [auditResults, setAuditResults] = useState<PageAuditResult[]>([])
  const [error, setError] = useState('')
  const [isInterStep, setIsInterStep] = useState(false)
  const [showProgressBar, setShowProgressBar] = useState(false)

  useEffect(() => {
    if (isInterStep) {
      const timer = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            clearInterval(timer)
            setIsInterStep(false)
            setStep(2)
            setShowProgressBar(false)
            return 100
          }
          return prevProgress + 1
        })
      }, 20)
      return () => clearInterval(timer)
    }
  }, [isInterStep])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prevData => ({ ...prevData, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (step === 1) {
      if (!isValidUrl(formData.url)) {
        setError('Please enter a valid URL')
        return
      }
      setError('')
      setIsInterStep(true)
      setProgress(0)
      setShowProgressBar(true)
    } else {
      if (!formData.name || !formData.email || !formData.phone) {
        setError('Please fill in all fields')
        return
      }
      setError('')
      setIsLoading(true)
      setProgress(0)
      try {
        const normalizedUrl = normalizeUrl(formData.url)
        const results = await performHolisticSEOAudit(normalizedUrl, setProgress)
        setAuditResults(results)
        setStep(3) // Set step to 3 when audit results are ready
        
        // Log form data to console (for debugging purposes)
        console.log('Form Data Submitted:', formData)
      } catch (err) {
        setError('An error occurred while performing the audit. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-mover-darkest to-mover-dark text-mover-lightest">
      <div className="circuit-overlay"></div>
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8 flex flex-col items-center justify-center">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-center">
          Free SEO Audit
        </h2>
        <p className="text-lg sm:text-xl mb-8 text-center max-w-2xl">
          Discover how to improve your website's SEO performance with our free basic SEO audit tool.
        </p>
        {step < 3 && (
          <form id="SEOAuditTool" onSubmit={handleSubmit} className="w-full max-w-md mb-8">
            <div className={`relative ${step !== 1 ? 'hidden' : ''}`}>
              <input
                type="text"
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                placeholder="Enter your website URL"
                className="input-primary w-full pr-12 rounded-lg"
              />
              <button type="submit" className="absolute right-2 top-1/2 transform -translate-y-1/2 text-mover-purple hover:text-mover-cyan transition-colors duration-300">
                <Search size={24} />
              </button>
            </div>
            <div className={step !== 2 ? 'hidden' : ''}>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your Name"
                className="input-primary w-full mb-4 rounded-lg"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Your Email"
                className="input-primary w-full mb-4 rounded-lg"
              />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Your Phone Number"
                className="input-primary w-full mb-4 rounded-lg"
              />
              <button type="submit" id="SEOSubmit" className="btn-primary w-full rounded-lg py-3">
                Get Your Free SEO Audit
              </button>
            </div>
            {error && <p className="text-mover-red mt-2 text-center">{error}</p>}
          </form>
        )}
        {showProgressBar && step === 1 && <ProgressBar progress={progress} isInterStep={isInterStep} />}
        {isLoading && <FullPageLoader />}
        {auditResults.length > 0 && <SEOAudit pageResults={auditResults} />}
      </main>
      <Footer />
    </div>
  )
}

export default App